import React from 'react'
import { format, formatDistance } from 'date-fns'
import { SimpleGrid, VStack, Text, Box } from '@chakra-ui/react'
import { FaCalendar, FaClock, FaMedkit, FaUserNurse } from 'react-icons/fa'

import {
    ClientInfo,
    PatientInfo,
    BreedFieldsFragment,
    ClientFieldsFragment,
    PatientFieldsFragment,
    AppointmentFieldsFragment,
    ClinicSpeciesFieldsFragment,
} from '~graphql/generated/graphql'
import EntityBox from './entity-box'
import {
    formatEmail,
    getClientFullName,
    stringIfExists,
} from '~utils/helpers'
import { formatPhoneReadable } from '~utils/phone'
import OutlineBox from '~components/shared/wrappers/outline-box'
import SharedStack from '~components/shared/wrappers/shared-stack'
import PendingActionDetail from '~components/pending-actions/pending-action-detail'
import { usePatientInfo } from '../patient-info-context'
import PreviewBox from './preview-box'
import { useClinic } from '~contexts/clinic-context'

interface Props {
    appointment: AppointmentFieldsFragment
    isMissingClient: boolean
    client: ClientFieldsFragment | undefined
    setClient: (client: ClientFieldsFragment | undefined) => void
    patient: PatientFieldsFragment | undefined
    setPatient: (patient: PatientFieldsFragment | undefined) => void
    isMakingNewClient: boolean
    setMakingNewClient: (isMakingNewClient: boolean) => void
    isMakingNewPatient: boolean
    setMakingNewPatient: (isMakingNewPatient: boolean) => void
}

export default function AppointmentDetailsSection({
    appointment,
    isMissingClient,
    client,
    setClient,
    patient,
    setPatient,
    isMakingNewClient,
    setMakingNewClient,
    isMakingNewPatient,
    setMakingNewPatient,
}: Props) {
    const { clinic } = useClinic()

    const {
        service,
        service_name,
        clinician,
        start_time,
        minutes,
        client_info,
        patient_info,
    } = appointment

    const { clinic_species, clinic_breed } = usePatientInfo()

    if (!client_info || !patient_info) {
        return <>No client or patient info found on linked appointment</>
    }

    return (
        <OutlineBox
            w="full"
            py={4}
            bg="white"
            zIndex={2}
            px={6}
            position="sticky"
            top={0}
            borderTopRadius={0}
            borderBottomRadius={32}
        >
            <SharedStack spacing={4}>
                <Text
                    fontWeight="bold"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    color="gray.600"
                >
                    Appointment Details
                </Text>
                <SimpleGrid w="full" columns={2} spacing={4}>
                    <PendingActionDetail
                        icon={FaMedkit}
                        label="Service"
                        value={stringIfExists(service?.name || service_name)}
                        color="brand.600"
                    />
                    <PendingActionDetail
                        icon={FaUserNurse}
                        label="Clinician"
                        value={stringIfExists(clinician?.name)}
                        color="brand.600"
                    />
                    <PendingActionDetail
                        icon={FaCalendar}
                        label="Date"
                        value={`${format(start_time, "PPP, 'at' p")}`}
                        helpText={formatDistance(start_time, new Date(), {
                            addSuffix: true,
                        })}
                        color="brand.600"
                    />
                    <PendingActionDetail
                        icon={FaClock}
                        label="Duration"
                        value={`${minutes} minutes`}
                        color="brand.600"
                    />
                </SimpleGrid>
                <VStack spacing={4} w="full">
                    <Box w="full">
                        <Text fontSize="sm" fontWeight="semibold" pl={1} mb={1}>
                            Client
                        </Text>
                        {isMissingClient ? (
                            <EntityBox
                                title={getClientName(client_info, client)}
                                hintText={[
                                    getClientEmail(client_info, client),
                                    getClientPhone(
                                        client_info,
                                        client,
                                        clinic?.address?.country,
                                    ),
                                ]}
                                isFilled={!!client || isMakingNewClient}
                                isMakingNew={isMakingNewClient}
                                onRemove={() => {
                                    setClient(undefined)
                                    setMakingNewClient(false)
                                }}
                            />
                        ) : (
                            <PreviewBox
                                title={getClientName(client_info, client)}
                                hintText={[
                                    getClientEmail(client_info, client),
                                    getClientPhone(
                                        client_info,
                                        client,
                                        clinic?.address?.country,
                                    ),
                                ]}
                            />
                        )}
                    </Box>
                    <Box w="full">
                        <Text fontSize="sm" fontWeight="semibold" pl={1} mb={1}>
                            Patient
                        </Text>
                        <EntityBox
                            title={getPatientName(patient_info, patient)}
                            hintText={[
                                getPatientSpeciesBreed(
                                    clinic_species,
                                    clinic_breed,
                                    patient,
                                ),
                            ]}
                            isFilled={
                                !!patient ||
                                isMakingNewPatient ||
                                isMakingNewClient
                            }
                            isMakingNew={
                                isMakingNewPatient || isMakingNewClient
                            }
                            onRemove={() => {
                                setPatient(undefined)
                                setMakingNewPatient(false)
                                if (isMakingNewClient) {
                                    setClient(undefined)
                                    setMakingNewClient(false)
                                }
                            }}
                        />
                    </Box>
                </VStack>
            </SharedStack>
        </OutlineBox>
    )
}

function getClientName(
    clientInfo: ClientInfo,
    client?: ClientFieldsFragment | null,
): string {
    return getClientFullName(client || clientInfo) || ''
}

function getClientEmail(
    clientInfo: ClientInfo,
    client?: ClientFieldsFragment | null,
): string {
    if (client) {
        return formatEmail(client.email) || ''
    }

    const { email } = clientInfo

    return formatEmail(email) || ''
}

function getClientPhone(
    clientInfo: ClientInfo,
    client?: ClientFieldsFragment | null,
    clinicCountry?: string | null,
): string {
    if (client) {
        return client.phone
            ? formatPhoneReadable(
                  client.phone,
                  client?.address?.country || clinicCountry,
              ) || ''
            : ''
    }

    const { phone } = clientInfo

    return phone ? formatPhoneReadable(phone, clinicCountry) || '' : ''
}

function getPatientName(
    patientInfo: PatientInfo,
    patient?: PatientFieldsFragment | null,
): string {
    if (patient) {
        return patient.name || ''
    }

    const { patient_name } = patientInfo

    return patient_name || ''
}

function getPatientSpeciesBreed(
    clinicSpecies: ClinicSpeciesFieldsFragment | undefined,
    clinicBreed: BreedFieldsFragment | undefined,
    patient?: PatientFieldsFragment | null,
): string {
    if (patient) {
        return `${patient.clinic_species?.species}, ${patient.clinic_breed?.description}`
    }

    return `${clinicSpecies?.species}, ${clinicBreed?.description}`
}
